import React from 'react';
import RemotePath from '../../consts/RemotePaths';
import gabrielaLogo from '../../assets/images/logo-full.png';
import ClinicalReportPhotos from './ClinicalReportPhotos';

/**
 * Generic Clinical Report PDF Template
 * 
 * Premium visual design aligned with Gabriela brand identity.
 * Multi-page template with header and footer repeated on every page.
 * Page 1: Patient info, treatment summary, current measurements, classification, evolution
 * Page 2+: History table, charts, and additional content
 */

// ─── Gabriela Brand Palette ───────────────────────────────────────
const palette = {
    // Text
    navy: '#1A2559',
    textDark: '#2D3748',
    textMedium: '#4A5568',
    textLight: '#718096',
    textMuted: '#A0AEC0',

    // Gabriela brand
    blue: '#5A80FB',
    blueLight: '#7B98FC',
    bluePale: '#E8EDFF',
    blueBg: '#F5F7FF',
    pink: '#ED3680',
    pinkLight: '#FFDFEC',
    pinkPale: '#FFF0F6',
    lavender: '#F0EDFF',
    lavenderLight: '#F8F7FF',
    purple: '#8B5CF6',
    purpleLight: '#EDE9FE',

    // Semantic
    successText: '#2E7D32',
    successBg: '#E8F5E9',
    successBorder: '#C8E6C9',
    warningText: '#E65100',
    warningBg: '#FFF3E0',
    warningBorder: '#FFE0B2',
    errorText: '#C62828',
    errorBg: '#FFEBEE',
    errorBorder: '#FFCDD2',
    infoText: '#1565C0',
    infoBg: '#E3F2FD',
    infoBorder: '#BBDEFB',

    // Surfaces
    white: '#FFFFFF',
    bgSubtle: '#FAFBFE',
    border: '#E8ECF4',
    borderLight: '#F0F3F9',
    divider: '#E2E8F0',

    // Direction colors
    directionUp: '#5A80FB',
    directionDown: '#E65100',
    directionNeutral: '#718096',
};

// ─── Severity Badge Helpers ───────────────────────────────────────
const getSeverityStyle = (severity) => {
    switch (severity) {
        case 'Normal':
            return { color: palette.successText, bg: palette.successBg, border: palette.successBorder };
        case 'Leve':
            return { color: '#F9A825', bg: '#FFFDE7', border: '#FFF9C4' };
        case 'Moderada':
            return { color: palette.warningText, bg: palette.warningBg, border: palette.warningBorder };
        case 'Severa':
            return { color: palette.errorText, bg: palette.errorBg, border: palette.errorBorder };
        case 'Muito severa':
            return { color: '#B71C1C', bg: '#FFCDD2', border: '#EF9A9A' };
        default:
            return { color: palette.textMedium, bg: palette.bgSubtle, border: palette.border };
    }
};

// ─── SVG Icons (clean outline style matching Gabriela Landing page) ──────────
const SvgIcon = ({ children, size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
        stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {children}
    </svg>
);

const icons = {
    brain: (color) => (
        <SvgIcon size={20} color={color || palette.blue}>
            <path d="M12 2a7 7 0 0 0-4.95 2.05A7 7 0 0 0 5 9c0 1.5.5 3 1.4 4.2L12 22l5.6-8.8A7 7 0 0 0 19 9a7 7 0 0 0-7-7z" />
            <circle cx="12" cy="9" r="3" />
        </SvgIcon>
    ),
    summary: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
            <rect x="9" y="3" width="6" height="4" rx="1" />
            <path d="M9 12h6M9 16h6" />
        </SvgIcon>
    ),
    measurements: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <path d="M2 12h4l3-9 4 18 3-9h4" />
        </SvgIcon>
    ),
    classification: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </SvgIcon>
    ),
    evolution: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </SvgIcon>
    ),
    history: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M9 21V9" />
        </SvgIcon>
    ),
    charts: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <path d="M18 20V10M12 20V4M6 20v-6" />
        </SvgIcon>
    ),
    patient: (color) => (
        <SvgIcon size={16} color={color || palette.blue}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </SvgIcon>
    ),
    calendar: (color) => (
        <SvgIcon size={16} color={color || palette.blue}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
        </SvgIcon>
    ),
    firstDate: (color) => (
        <SvgIcon size={14} color={color || palette.blue}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
        </SvgIcon>
    ),
    lastDate: (color) => (
        <SvgIcon size={14} color={color || palette.blue}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
            <path d="M9 16l2 2 4-4" />
        </SvgIcon>
    ),
    duration: (color) => (
        <SvgIcon size={14} color={color || palette.blue}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
        </SvgIcon>
    ),
    total: (color) => (
        <SvgIcon size={14} color={color || palette.blue}>
            <path d="M18 20V10M12 20V4M6 20v-6" />
        </SvgIcon>
    ),
    shield: (color) => (
        <SvgIcon size={20} color={color || palette.purple}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
        </SvgIcon>
    ),
    photos: (color) => (
        <SvgIcon size={14} color={color || palette.textMedium}>
            <rect x="3" y="6" width="18" height="14" rx="2" />
            <path d="M8 6l1.5-2h5L16 6" />
            <circle cx="12" cy="13" r="3.5" />
        </SvgIcon>
    ),
};

const ClinicalReportTemplate = React.forwardRef(({
    companyData,
    patientName,
    reportTitle,
    assessmentDate,
    measurements,
    classification,
    treatmentSummary,
    measurementChanges,
    historyTable,
    photoReport,
    children,
}, ref) => {

    const logoSrc = companyData?.Logo
        ? (companyData.Logo.startsWith('data:')
            ? companyData.Logo
            : (companyData.Id
                ? RemotePath.PATH_USER_UPLOADS_COMPANY_LOGO(companyData.Id) + companyData.Logo
                : ''))
        : '';

    const companyName = companyData?.Name || '';
    const companyPhone = companyData?.Phone || '';
    const companyCNPJ = companyData?.CNPJ || '';

    const fullAddress = companyData
        ? [
            companyData.Address,
            companyData.Number,
            companyData.Complement,
            companyData.District,
            companyData.City,
            companyData.State,
        ].filter(Boolean).join(', ')
        : '';

    const getDirectionColor = (direction) => {
        if (direction === 'up') return palette.directionUp;
        if (direction === 'down') return palette.directionDown;
        return palette.directionNeutral;
    };

    const getDirectionArrow = (direction) => {
        if (direction === 'up') return '↑';
        if (direction === 'down') return '↓';
        return '=';
    };

    const hasSecondPage = (historyTable?.rows?.length > 0) || photoReport || children;

    const firstPageStyle = hasSecondPage
        ? { ...s.page, height: '297mm', overflow: 'hidden' }
        : s.page;

    // ─── Reusable Header ──────────────────────────────────────────
    const PageHeader = () => (
        <>
            <div style={s.header}>
                {logoSrc && (
                    <img
                        src={logoSrc}
                        alt={companyName}
                        style={s.clinicLogo}
                        crossOrigin="anonymous"
                    />
                )}
                <div style={s.clinicInfo}>
                    <span style={s.clinicName}>{companyName}</span>
                    {companyCNPJ && <span style={s.clinicDetail}>CNPJ: {companyCNPJ}</span>}
                    {fullAddress && <span style={s.clinicDetail}>{fullAddress}</span>}
                    {companyPhone && <span style={s.clinicDetail}>Tel: {companyPhone}</span>}
                </div>
            </div>
            <div style={s.headerDivider} />
        </>
    );

    // ─── Reusable Footer ──────────────────────────────────────────
    const PageFooter = ({ isFirstPage }) => (
        <div style={isFirstPage ? s.footer : s.continuationFooter}>
            <div style={s.footerDivider} />
            <div style={s.footerContent}>
                <span style={s.footerDate}>
                    Relatório gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <div style={s.footerCenter}>
                    <img src={gabrielaLogo} alt="Gabriela" style={s.footerLogo} />
                    <div style={s.footerTextBlock}>
                        <span style={s.footerTagline}>Gestão de consultórios e clínicas</span>
                        <span style={s.footerLink}>www.gabriela.app.br</span>
                    </div>
                </div>
            </div>
        </div>
    );

    // ─── Section Title ────────────────────────────────────────────
    const SectionTitle = ({ icon, children: titleText, color }) => (
        <div style={s.sectionTitleRow}>
            <span style={{ ...s.sectionIcon, color: color || palette.blue }}>{icon}</span>
            <h3 style={{ ...s.sectionTitle, color: color || palette.blue }}>{titleText}</h3>
        </div>
    );

    // ─── Severity Badge ───────────────────────────────────────────
    const SeverityBadge = ({ severity }) => {
        const sv = getSeverityStyle(severity);
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '10px',
                fontWeight: 600,
                color: sv.color,
                backgroundColor: sv.bg,
                border: `1px solid ${sv.border}`,
                marginLeft: '6px',
                whiteSpace: 'nowrap',
            }}>
                {severity}
            </span>
        );
    };

    return (
        <div ref={ref} style={s.wrapper}>
            {/* ═══════════════════ PAGE 1 ═══════════════════ */}
            <div style={firstPageStyle}>
                <PageHeader />

                {/* Report Title */}
                <div style={s.titleContainer}>
                    <h1 style={s.title}>{reportTitle}</h1>
                </div>

                {/* Patient Info */}
                <div style={s.patientInfoContainer}>
                    <span style={s.patientIcon}>{icons.patient()}</span>
                    <div style={s.patientInfoText}>
                        <span style={s.patientLabel}>Paciente</span>
                        <span style={s.patientName}>{patientName}</span>
                    </div>
                </div>

                {/* Treatment Summary */}
                {treatmentSummary && (
                    <div style={s.section}>
                        <SectionTitle icon={icons.summary()} color={palette.textMedium}>Resumo do Acompanhamento</SectionTitle>
                        <div style={s.summaryGrid}>
                            {[
                                { icon: icons.firstDate(), label: 'PRIMEIRA AVALIAÇÃO', value: treatmentSummary.firstDate },
                                { icon: icons.lastDate(), label: 'ÚLTIMA AVALIAÇÃO', value: treatmentSummary.lastDate },
                                { icon: icons.duration(), label: 'DURAÇÃO DO TRATAMENTO', value: treatmentSummary.duration },
                                { icon: icons.total(), label: 'TOTAL DE AVALIAÇÕES', value: treatmentSummary.totalAssessments },
                            ].map((item, i) => (
                                <div key={i} style={s.summaryCard}>
                                    <div style={s.summaryCardIcon}>{item.icon}</div>
                                    <span style={s.summaryLabel}>{item.label}</span>
                                    <span style={s.summaryValue}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Measurements */}
                {measurements && measurements.length > 0 && (
                    <div style={s.section}>
                        <SectionTitle icon={icons.measurements()} color={palette.textMedium}>Medições Atuais</SectionTitle>
                        <div style={s.measurementsGrid}>
                            {measurements.map((m, index) => {
                                const accentColor = m.isIndex ? palette.pink : palette.blue;
                                return (
                                    <div key={index} style={{
                                        ...s.measurementCard,
                                        borderLeft: `3px solid ${accentColor}20`,
                                    }}>
                                        <span style={s.measurementLabel}>{m.label}</span>
                                        <div style={s.measurementValueRow}>
                                            <span style={s.measurementValue}>{m.value}</span>
                                            {m.severity && <SeverityBadge severity={m.severity} />}
                                        </div>
                                        {m.description && (
                                            <span style={s.measurementDescription}>{m.description}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Classification */}
                {classification && (
                    <div style={s.section}>
                        <SectionTitle icon={icons.classification()} color={palette.textMedium}>Classificação</SectionTitle>
                        <div style={s.classificationCard}>
                            <div style={s.classificationIconCircle}>{icons.shield()}</div>
                            <div style={s.classificationText}>
                                <span style={s.classificationMain}>{classification}</span>
                                <span style={s.classificationSub}>De acordo com os índices clínicos avaliados nesta consulta.</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Measurement Changes */}
                {measurementChanges && measurementChanges.length > 0 && (
                    <div style={s.section}>
                        <SectionTitle icon={icons.evolution()} color={palette.textMedium}>Evolução desde a Primeira Avaliação</SectionTitle>
                        <div style={s.changesGrid}>
                            {measurementChanges.map((change, index) => (
                                <div key={index} style={{
                                    ...s.changeCard,
                                    borderLeft: `3px solid ${getDirectionColor(change.direction)}20`,
                                }}>
                                    <span style={s.changeLabel}>{change.label}</span>
                                    <div style={s.changeValues}>
                                        <div style={s.changeRow}>
                                            <span style={s.changeSubLabel}>Inicial</span>
                                            <span style={s.changeSubValue}>{change.initialValue}</span>
                                        </div>
                                        <div style={s.changeRow}>
                                            <span style={s.changeSubLabel}>Atual</span>
                                            <span style={s.changeSubValue}>{change.currentValue}</span>
                                        </div>
                                    </div>
                                    <div style={{
                                        ...s.changeDiff,
                                        color: getDirectionColor(change.direction),
                                    }}>
                                        <span style={{ fontSize: '14px' }}>{getDirectionArrow(change.direction)}</span>
                                        <span>{change.difference}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <PageFooter isFirstPage={true} />
            </div>

            {/* ═══════════════════ PAGE 2+ ═══════════════════ */}
            {hasSecondPage && (
                <div style={s.continuationPage}>
                    <PageHeader />

                    {/* History Table */}
                    {historyTable && historyTable.rows && historyTable.rows.length > 0 && (
                        <div style={s.section}>
                            <SectionTitle icon={icons.history()} color={palette.textMedium}>Histórico de Avaliações</SectionTitle>
                            <div style={s.tableContainer}>
                                <table style={s.table}>
                                    <thead>
                                        <tr>
                                            {historyTable.columns.map((col, i) => (
                                                <th key={i} style={s.th}>{col.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyTable.rows.map((row, rowIndex) => {
                                            const isLatest = row.isLatest;
                                            return (
                                                <tr key={rowIndex} style={
                                                    isLatest
                                                        ? s.latestRow
                                                        : rowIndex % 2 === 0
                                                            ? s.evenRow
                                                            : {}
                                                }>
                                    {historyTable.columns.map((col, colIndex) => {
                                        const cellValue = row[col.key];
                                        const isSeverityCol = col.key.toLowerCase().includes('severity') || col.key === 'classification';
                                        const isDateCol = col.key === 'date';
                                        return (
                                            <td key={colIndex} style={{
                                                ...s.td,
                                                ...(isLatest ? s.latestTd : {}),
                                            }}>
                                                {isDateCol && isLatest && (
                                                    <span style={s.latestDot}>●</span>
                                                )}
                                                {isSeverityCol && cellValue ? (
                                                    <SeverityBadge severity={cellValue} />
                                                ) : (
                                                    cellValue
                                                )}
                                            </td>
                                        );
                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div style={s.tableLegend}>
                                <span style={{ ...s.latestDot, position: 'static', marginRight: '4px' }}>●</span>
                                <span style={s.tableLegendText}>Avaliação mais recente</span>
                            </div>
                        </div>
                    )}

                    {/* Additional content (e.g., charts) */}
                    {children}

                    {/* Registro fotográfico (opcional) */}
                    {photoReport && (
                        <div style={s.section}>
                            <SectionTitle icon={icons.photos()} color={palette.textMedium}>Registro Fotográfico</SectionTitle>
                            <ClinicalReportPhotos photoReport={photoReport} palette={palette} />
                        </div>
                    )}

                    <PageFooter isFirstPage={false} />
                </div>
            )}
        </div>
    );
});

ClinicalReportTemplate.displayName = 'ClinicalReportTemplate';

// ─── Styles ───────────────────────────────────────────────────────
const s = {
    wrapper: {
        backgroundColor: palette.white,
    },
    page: {
        width: '210mm',
        minHeight: '297mm',
        padding: '15mm 16mm',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        color: palette.textDark,
        backgroundColor: palette.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    },
    continuationPage: {
        width: '210mm',
        padding: '15mm 16mm',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        color: palette.textDark,
        backgroundColor: palette.white,
        boxSizing: 'border-box',
    },

    // ─── Header ───────────────────────────────────────────────────
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px',
    },
    clinicLogo: {
        width: '52px',
        height: '52px',
        objectFit: 'contain',
        borderRadius: '8px',
    },
    clinicInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    clinicName: {
        fontSize: '16px',
        fontWeight: 700,
        color: palette.textDark,
        marginBottom: '3px',
    },
    clinicDetail: {
        fontSize: '10px',
        color: palette.textLight,
        lineHeight: '1.5',
    },
    headerDivider: {
        height: '2px',
        background: `linear-gradient(90deg, ${palette.pink}40, ${palette.blue}40, ${palette.pink}20)`,
        margin: '8px 0 14px 0',
        borderRadius: '1px',
    },

    // ─── Title ────────────────────────────────────────────────────
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        backgroundColor: palette.blueBg,
        borderRadius: '10px',
        marginBottom: '14px',
    },
    titleIcon: {
        fontSize: '22px',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.bluePale,
        borderRadius: '10px',
        flexShrink: 0,
    },
    title: {
        fontSize: '18px',
        fontWeight: 700,
        color: palette.navy,
        margin: 0,
    },

    // ─── Patient Info ─────────────────────────────────────────────
    patientInfoContainer: {
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
        padding: '12px 16px',
        backgroundColor: palette.blueBg,
        borderRadius: '10px',
        border: `1px solid ${palette.bluePale}`,
    },
    patientInfoLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 2,
    },
    patientInfoRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        borderLeft: `1px solid ${palette.bluePale}`,
        paddingLeft: '12px',
    },
    patientIcon: {
        fontSize: '18px',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.white,
        borderRadius: '8px',
        flexShrink: 0,
    },
    patientInfoText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
    },
    patientLabel: {
        fontSize: '9px',
        color: palette.textLight,
        fontWeight: 500,
    },
    patientName: {
        fontSize: '14px',
        fontWeight: 700,
        color: palette.navy,
    },
    patientDate: {
        fontSize: '14px',
        fontWeight: 700,
        color: palette.navy,
    },

    // ─── Sections ─────────────────────────────────────────────────
    section: {
        marginBottom: '14px',
    },
    sectionTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '8px',
        paddingBottom: '4px',
        borderBottom: `1px solid ${palette.borderLight}`,
    },
    sectionIcon: {
        fontSize: '13px',
        flexShrink: 0,
    },
    sectionTitle: {
        fontSize: '12px',
        fontWeight: 600,
        margin: 0,
        letterSpacing: '0.3px',
    },

    // ─── Summary Cards ────────────────────────────────────────────
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: '8px',
    },
    summaryCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '3px',
        padding: '10px 8px',
        backgroundColor: palette.white,
        borderRadius: '8px',
        border: `1px solid ${palette.border}`,
        textAlign: 'center',
    },
    summaryCardIcon: {
        fontSize: '16px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.blueBg,
        borderRadius: '50%',
        marginBottom: '2px',
    },
    summaryLabel: {
        fontSize: '7px',
        color: palette.textLight,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    summaryValue: {
        fontSize: '13px',
        color: palette.navy,
        fontWeight: 700,
    },

    // ─── Measurements ─────────────────────────────────────────────
    measurementsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '8px',
    },
    measurementCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px 10px',
        backgroundColor: palette.white,
        borderRadius: '8px',
        border: `1px solid ${palette.border}`,
    },
    measurementLabel: {
        fontSize: '9px',
        color: palette.textLight,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
    },
    measurementValueRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    measurementValue: {
        fontSize: '15px',
        color: palette.navy,
        fontWeight: 700,
    },
    measurementDescription: {
        fontSize: '8px',
        color: palette.textMuted,
        fontWeight: 400,
    },

    // ─── Classification ───────────────────────────────────────────
    classificationCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: palette.lavenderLight,
        borderRadius: '10px',
        border: `1px solid ${palette.lavender}`,
    },
    classificationIconCircle: {
        fontSize: '20px',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.purpleLight,
        borderRadius: '50%',
        flexShrink: 0,
    },
    classificationText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: 1,
    },
    classificationMain: {
        fontSize: '14px',
        fontWeight: 700,
        color: palette.navy,
    },
    classificationSub: {
        fontSize: '9px',
        color: palette.textLight,
        fontWeight: 400,
    },

    // ─── Measurement Changes ──────────────────────────────────────
    changesGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '8px',
    },
    changeCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '8px 10px',
        backgroundColor: palette.white,
        borderRadius: '8px',
        border: `1px solid ${palette.border}`,
    },
    changeLabel: {
        fontSize: '9px',
        color: palette.textLight,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        marginBottom: '2px',
    },
    changeValues: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    changeRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    changeSubLabel: {
        fontSize: '9px',
        color: palette.textMuted,
        fontWeight: 500,
    },
    changeSubValue: {
        fontSize: '11px',
        color: palette.textDark,
        fontWeight: 600,
    },
    changeDiff: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        fontSize: '12px',
        fontWeight: 700,
        paddingTop: '4px',
        borderTop: `1px solid ${palette.borderLight}`,
        marginTop: '2px',
    },

    // ─── History Table ────────────────────────────────────────────
    tableContainer: {
        borderRadius: '8px',
        border: `1px solid ${palette.border}`,
        overflow: 'hidden',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '10px',
    },
    th: {
        textAlign: 'center',
        padding: '8px 5px',
        backgroundColor: palette.blueBg,
        borderBottom: `1px solid ${palette.bluePale}`,
        fontWeight: 600,
        color: palette.textMedium,
        fontSize: '9px',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
    },
    td: {
        textAlign: 'center',
        padding: '6px 5px',
        borderBottom: `1px solid ${palette.borderLight}`,
        color: palette.textDark,
        fontSize: '10px',
        position: 'relative',
    },
    evenRow: {
        backgroundColor: palette.bgSubtle,
    },
    latestRow: {
        backgroundColor: '#EFF6FF',
    },
    latestTd: {
        fontWeight: 600,
    },
    latestDot: {
        color: palette.blue,
        fontSize: '8px',
        position: 'relative',
        marginRight: '2px',
    },
    tableLegend: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        marginTop: '6px',
        paddingLeft: '4px',
    },
    tableLegendText: {
        fontSize: '8px',
        color: palette.textLight,
        fontWeight: 500,
    },

    // ─── Footer ───────────────────────────────────────────────────
    footer: {
        marginTop: 'auto',
        paddingTop: '12px',
    },
    continuationFooter: {
        paddingTop: '16px',
    },
    footerDivider: {
        height: '1px',
        backgroundColor: palette.border,
        marginBottom: '10px',
    },
    footerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    footerDate: {
        fontSize: '9px',
        color: palette.textMuted,
        fontWeight: 400,
    },
    footerCenter: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    footerLogo: {
        height: '28px',
        objectFit: 'contain',
        opacity: 0.8,
    },
    footerTextBlock: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '1px',
    },
    footerBrand: {
        fontSize: '11px',
        fontWeight: 700,
        color: palette.navy,
    },
    footerTagline: {
        fontSize: '9px',
        color: palette.textLight,
        lineHeight: '1.3',
    },
    footerLink: {
        fontSize: '9px',
        color: palette.blue,
        textDecoration: 'none',
        lineHeight: '1.3',
    },
};

export default ClinicalReportTemplate;
